/**
 * "도움요청 관리" — OZK 도움연결 MVP admin queue (P0.5, docs/OZK_FINAL_LAUNCH_STRATEGY.md 12장).
 *
 * This is the only new admin screen 12장 asks for. It does not implement a
 * new business-contact system: the "업체에 전달" action here calls
 * POST /api/help-requests/:id/match, which reuses the existing
 * business_inquiries insert + notification pipeline
 * (functions/api/businesses/[id]/inquiries.ts already owns that flow).
 */
import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { CheckCircle2, MessageCircleQuestion, MessageSquareReply, Search, Send, ShieldQuestion, Sparkles, XCircle } from "lucide-react";
import {
  buildRecommendationCopy,
  helpConnectionAdminManualReplyCopy,
  HELP_CONNECTION_CATEGORIES,
  type HelpConnectionAiSuggestion,
  type HelpConnectionCategoryId,
} from "@shared/help-connection-copy";
import { apiGet, apiPatch, apiPost } from "./api";
import { queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";

type HelpRequestStatus =
  | "submitted"
  | "needs_information"
  | "guidance_sent"
  | "awaiting_consent"
  | "ready_to_match"
  | "matched"
  | "sent_to_business"
  | "business_accepted"
  | "customer_contacted"
  | "resolved"
  | "closed";

type HelpRequest = {
  id: number;
  referenceCode: string;
  userId: number;
  userDisplayName: string;
  topicId: string | null;
  zoneId: string | null;
  suburb: string | null;
  sourceType: string | null;
  sourceId: string | null;
  originalQuestion: string;
  structuredSummary: string | null;
  riskLevel: "R1" | "R2" | "R3" | "R4";
  status: HelpRequestStatus;
  consentToShare: boolean;
  consentedAt: string | null;
  createdAt: string;
};

type HelpRequestsResponse = { items: HelpRequest[]; count: number };

type BusinessOption = {
  id: string;
  businessName: string;
  businessNameKo: string | null;
  suburb: string | null;
  state: string | null;
};

// P3 반자동 매칭 (11장/12장) - functions/utils/help-request-matching.ts's
// rule-based candidates, shown as one-click shortcuts above manual search.
type MatchCandidate = {
  businessId: string;
  businessName: string;
  businessNameKo: string | null;
  partnerStatus: string;
  matchedOn: string[];
  responseSlaHours: number | null;
};

type ClassifyResponse =
  | { ok: true; method: "ai"; suggestion: HelpConnectionAiSuggestion }
  | { ok: true; method: "unavailable"; reason: string };

const statusOptions: { value: HelpRequestStatus | "all"; label: string }[] = [
  { value: "all", label: "전체" },
  { value: "submitted", label: "새 요청" },
  { value: "needs_information", label: "추가정보 필요" },
  { value: "guidance_sent", label: "답변 준비" },
  { value: "awaiting_consent", label: "동의 대기" },
  { value: "ready_to_match", label: "업체 연결 대기" },
  { value: "matched", label: "업체 매칭됨" },
  { value: "sent_to_business", label: "업체 전달" },
  { value: "business_accepted", label: "업체 수락" },
  { value: "customer_contacted", label: "고객 연락" },
  { value: "resolved", label: "해결" },
  { value: "closed", label: "종료" },
];
const statusLabel: Record<string, string> = Object.fromEntries(
  statusOptions.map((o) => [o.value, o.label]),
);
const riskLabel: Record<string, string> = {
  R1: "R1 · 일반",
  R2: "R2 · 주의",
  R3: "R3 · 위험",
  R4: "R4 · 고위험(자동매칭 금지)",
};

export default function HelpRequestsManagement() {
  const { toast } = useToast();
  const [status, setStatus] = useState<string>("all");
  const [matchTarget, setMatchTarget] = useState<HelpRequest | null>(null);
  const [businessQuery, setBusinessQuery] = useState("");
  const [businessResults, setBusinessResults] = useState<BusinessOption[]>([]);
  const [businessSearching, setBusinessSearching] = useState(false);
  const [selectedBusiness, setSelectedBusiness] = useState<BusinessOption | null>(null);
  const [matchReason, setMatchReason] = useState("");
  const [guidanceTarget, setGuidanceTarget] = useState<HelpRequest | null>(null);
  const [guidanceCategoryId, setGuidanceCategoryId] = useState<HelpConnectionCategoryId | "">("");
  const [guidanceMessage, setGuidanceMessage] = useState("");
  const [classifySuggestion, setClassifySuggestion] = useState<HelpConnectionAiSuggestion | null>(null);

  const list = useQuery({
    queryKey: ["admin-help-requests", status],
    queryFn: () =>
      apiGet<HelpRequestsResponse>(
        `/api/help-requests${status !== "all" ? `?status=${status}` : ""}`,
      ),
  });

  const patch = useMutation({
    mutationFn: ({ id, body }: { id: number; body: Record<string, unknown> }) =>
      apiPatch(`/api/help-requests/${id}`, body),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["admin-help-requests"] });
      if (variables.body.guidanceMessage) {
        toast({ title: "답변을 보냈습니다", description: "고객에게 인앱 알림으로 전달됩니다." });
        closeGuidanceDialog();
      } else {
        toast({ title: "도움요청을 갱신했습니다" });
      }
    },
    onError: (error: Error) => toast({ title: "갱신 실패", description: error.message, variant: "destructive" }),
  });

  const match = useMutation({
    mutationFn: ({ id, businessId, reason }: { id: number; businessId: string; reason: string }) =>
      apiPost(`/api/help-requests/${id}/match`, { businessId, matchReason: reason || undefined }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-help-requests"] });
      toast({ title: "업체에 전달했습니다", description: "기존 business_inquiries 흐름으로 알림이 발송됩니다." });
      closeMatchDialog();
    },
    onError: (error: Error) => toast({ title: "업체 전달 실패", description: error.message, variant: "destructive" }),
  });

  // P3 반자동 매칭 (11장/12장) - rule-based candidates for the currently
  // open match dialog, shown as one-click shortcuts above manual search.
  const candidates = useQuery({
    queryKey: ["admin-help-request-candidates", matchTarget?.id],
    queryFn: () => apiGet<{ ok: true; items: MatchCandidate[] }>(`/api/help-requests/${matchTarget!.id}/candidates`),
    enabled: !!matchTarget,
  });

  const classify = useMutation({
    mutationFn: (id: number) => apiPost<ClassifyResponse>(`/api/help-requests/${id}/classify`, {}),
    onSuccess: (data) => {
      if (data.method === "unavailable") {
        setClassifySuggestion(null);
        toast({ title: "AI 분류 제안 없음", description: data.reason });
        return;
      }
      setClassifySuggestion(data.suggestion);
      toast({
        title: "AI 분류 제안을 받았습니다",
        description: "내용을 확인하고 필요하면 수정한 뒤 적용하세요 (자동 적용되지 않습니다).",
      });
    },
    onError: (error: Error) => toast({ title: "AI 분류 실패", description: error.message, variant: "destructive" }),
  });

  const applyClassifySuggestion = () => {
    if (!classifySuggestion || !guidanceTarget) return;
    if (classifySuggestion.categoryId !== "unknown") {
      setGuidanceCategoryId(classifySuggestion.categoryId);
    }
    setGuidanceMessage(classifySuggestion.safeFirstResponse || guidanceMessage);
    patch.mutate({ id: guidanceTarget.id, body: { riskLevel: classifySuggestion.riskLevel } });
  };

  const openMatchDialog = (request: HelpRequest) => {
    setMatchTarget(request);
    setBusinessQuery("");
    setBusinessResults([]);
    setSelectedBusiness(null);
    setMatchReason("");
  };
  const closeMatchDialog = () => {
    setMatchTarget(null);
    setBusinessResults([]);
    setSelectedBusiness(null);
    setMatchReason("");
  };

  const openGuidanceDialog = (request: HelpRequest) => {
    setGuidanceTarget(request);
    setGuidanceCategoryId("");
    setGuidanceMessage(helpConnectionAdminManualReplyCopy.guidanceSent);
    setClassifySuggestion(null);
  };
  const closeGuidanceDialog = () => {
    setGuidanceTarget(null);
    setGuidanceCategoryId("");
    setGuidanceMessage("");
    setClassifySuggestion(null);
  };

  const searchBusinesses = async () => {
    if (!businessQuery.trim()) return;
    setBusinessSearching(true);
    try {
      const results = await apiGet<BusinessOption[]>(
        `/api/business-directory?q=${encodeURIComponent(businessQuery.trim())}&limit=10`,
      );
      setBusinessResults(Array.isArray(results) ? results : []);
    } catch (error: any) {
      toast({ title: "업체 검색 실패", description: error?.message, variant: "destructive" });
    } finally {
      setBusinessSearching(false);
    }
  };

  const items = list.data?.items || [];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">도움요청 관리</h2>
        <p className="text-sm text-muted-foreground">
          업체를 아직 정하지 않은 질문(OZK 도움요청)을 확인하고, 준비되면 기존 업체문의 흐름(business_inquiries)으로 전달합니다.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <MessageCircleQuestion className="h-4 w-4" />큐 ({items.length}건)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-52"><SelectValue /></SelectTrigger>
            <SelectContent>
              {statusOptions.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>문의번호</TableHead>
                  <TableHead>사용자 / 유입</TableHead>
                  <TableHead>질문</TableHead>
                  <TableHead>위험도</TableHead>
                  <TableHead>동의</TableHead>
                  <TableHead>상태</TableHead>
                  <TableHead className="text-right">작업</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((request) => (
                  <TableRow key={request.id}>
                    <TableCell>
                      <div className="font-medium">{request.referenceCode}</div>
                      <div className="text-xs text-muted-foreground">
                        {new Date(request.createdAt).toLocaleString("ko-KR")}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>{request.userDisplayName}</div>
                      <div className="text-xs text-muted-foreground">
                        {request.sourceType || "-"}{request.topicId ? ` · ${request.topicId}` : ""}
                      </div>
                      <div className="text-xs text-muted-foreground">{request.suburb || ""}</div>
                    </TableCell>
                    <TableCell className="max-w-xs">
                      <div className="truncate" title={request.originalQuestion}>{request.originalQuestion}</div>
                      {request.structuredSummary && (
                        <div className="mt-1 truncate text-xs text-muted-foreground" title={request.structuredSummary}>
                          요약: {request.structuredSummary}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Select
                        value={request.riskLevel}
                        onValueChange={(value) => patch.mutate({ id: request.id, body: { riskLevel: value } })}
                      >
                        <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {(["R1", "R2", "R3", "R4"] as const).map((r) => (
                            <SelectItem key={r} value={r}>{riskLabel[r]}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      {request.consentToShare ? (
                        <Badge variant="secondary" className="gap-1">
                          <CheckCircle2 className="h-3 w-3" />동의함
                        </Badge>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => patch.mutate({ id: request.id, body: { consentToShare: true } })}
                        >
                          <ShieldQuestion className="mr-1 h-3 w-3" />동의 확인
                        </Button>
                      )}
                    </TableCell>
                    <TableCell>
                      <Select
                        value={request.status}
                        onValueChange={(value) => patch.mutate({ id: request.id, body: { status: value } })}
                      >
                        <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {statusOptions.filter((o) => o.value !== "all").map((o) => (
                            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="outline" onClick={() => openGuidanceDialog(request)}>
                          <MessageSquareReply className="mr-1 h-4 w-4" />답변 보내기
                        </Button>
                        <Button
                          size="sm"
                          disabled={!request.consentToShare}
                          title={!request.consentToShare ? "고객 동의 확인 후 전달할 수 있습니다" : undefined}
                          onClick={() => openMatchDialog(request)}
                        >
                          <Send className="mr-1 h-4 w-4" />업체에 전달
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {!list.isLoading && items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="py-10 text-center text-muted-foreground">
                      해당 상태의 도움요청이 없습니다.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={!!matchTarget} onOpenChange={(open) => { if (!open) closeMatchDialog(); }}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>업체에 전달</DialogTitle>
            <DialogDescription>
              {matchTarget?.referenceCode} 건을 업체에 전달합니다. business_inquiries에 새 문의로 등록되고, 업체에는 기존 알림(DM·카카오·인앱)이 그대로 발송됩니다.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            {(candidates.data?.items?.length ?? 0) > 0 && (
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">
                  추천 후보 (Topic·카테고리·지역이 일치하는 Founding Partner/Premium 업체, 규칙 기반)
                </Label>
                <div className="space-y-1">
                  {candidates.data!.items.map((c) => (
                    <button
                      key={c.businessId}
                      type="button"
                      onClick={() =>
                        setSelectedBusiness({
                          id: c.businessId,
                          businessName: c.businessName,
                          businessNameKo: c.businessNameKo,
                          suburb: null,
                          state: null,
                        })
                      }
                      className={`flex w-full items-center justify-between rounded border p-2 text-left text-sm hover:bg-muted ${selectedBusiness?.id === c.businessId ? "bg-muted" : ""}`}
                    >
                      <span className="font-medium">{c.businessNameKo || c.businessName}</span>
                      <Badge variant={c.partnerStatus === "premium" ? "default" : "secondary"} className="text-xs">
                        {c.partnerStatus === "premium" ? "Premium" : "Founding Partner"}
                      </Badge>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-2">
              <Input
                value={businessQuery}
                onChange={(e) => setBusinessQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") searchBusinesses(); }}
                placeholder="추천 후보에 없으면 업체명으로 직접 검색"
              />
              <Button type="button" variant="outline" onClick={searchBusinesses} disabled={businessSearching}>
                <Search className="mr-1 h-4 w-4" />검색
              </Button>
            </div>

            {businessResults.length > 0 && (
              <div className="max-h-48 space-y-1 overflow-y-auto rounded-md border p-1">
                {businessResults.map((b) => (
                  <button
                    key={b.id}
                    type="button"
                    onClick={() => setSelectedBusiness(b)}
                    className={`w-full rounded p-2 text-left text-sm hover:bg-muted ${selectedBusiness?.id === b.id ? "bg-muted" : ""}`}
                  >
                    <div className="font-medium">{b.businessNameKo || b.businessName}</div>
                    <div className="text-xs text-muted-foreground">{b.suburb || ""} {b.state || ""}</div>
                  </button>
                ))}
              </div>
            )}

            {selectedBusiness && (
              <div className="rounded-md border bg-muted/40 p-3 text-sm">
                선택된 업체: <span className="font-medium">{selectedBusiness.businessNameKo || selectedBusiness.businessName}</span>
              </div>
            )}

            <div className="space-y-1">
              <Label htmlFor="match-reason">선정 사유 (선택)</Label>
              <Textarea
                id="match-reason"
                value={matchReason}
                onChange={(e) => setMatchReason(e.target.value)}
                placeholder="예: 지역·업종이 일치하고 최근 응답률이 높음"
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={closeMatchDialog}>
              <XCircle className="mr-1 h-4 w-4" />취소
            </Button>
            <Button
              type="button"
              disabled={!selectedBusiness || match.isPending}
              onClick={() =>
                matchTarget &&
                selectedBusiness &&
                match.mutate({ id: matchTarget.id, businessId: selectedBusiness.id, reason: matchReason.trim() })
              }
            >
              <Send className="mr-1 h-4 w-4" />전달 확정
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!guidanceTarget} onOpenChange={(open) => { if (!open) closeGuidanceDialog(); }}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>답변 보내기</DialogTitle>
            <DialogDescription>
              {guidanceTarget?.referenceCode} 건에 인앱 알림으로 답변을 보냅니다. 아직 완료된 연결 실적이 없으므로, 업체 추천 문구는 항상 "확인해드릴게요" 수준의 절제된 표현만 사용합니다.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="flex items-center justify-between rounded-md border border-dashed p-2">
              <p className="text-xs text-muted-foreground">
                AI가 업종·위험도·안전한 첫 응답 초안을 제안합니다 (자동 적용 안 됨, 검토 후 적용).
              </p>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={!guidanceTarget || classify.isPending}
                onClick={() => guidanceTarget && classify.mutate(guidanceTarget.id)}
              >
                <Sparkles className="mr-1 h-4 w-4" />
                {classify.isPending ? "분류 중..." : "AI 분류 제안"}
              </Button>
            </div>

            {classifySuggestion && (
              <div className="space-y-1 rounded-md border bg-muted/40 p-3 text-sm">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="secondary">
                    {classifySuggestion.categoryId === "unknown"
                      ? "업종 미확신"
                      : HELP_CONNECTION_CATEGORIES.find((c) => c.id === classifySuggestion.categoryId)?.label}
                  </Badge>
                  <Badge variant="secondary">{riskLabel[classifySuggestion.riskLevel]}</Badge>
                  <span className="text-xs text-muted-foreground">
                    확신도 {Math.round(classifySuggestion.confidence * 100)}%
                  </span>
                </div>
                {classifySuggestion.suggestedFollowUpQuestions.length > 0 && (
                  <p className="text-xs text-muted-foreground">
                    추가로 물어볼 것: {classifySuggestion.suggestedFollowUpQuestions.join(" / ")}
                  </p>
                )}
                <Button type="button" size="sm" onClick={applyClassifySuggestion}>
                  이 제안 적용하기
                </Button>
              </div>
            )}

            <div className="space-y-1">
              <Label>업종 (선택 — 추천 문구 채우기용)</Label>
              <Select
                value={guidanceCategoryId}
                onValueChange={(value) => {
                  const categoryId = value as HelpConnectionCategoryId;
                  setGuidanceCategoryId(categoryId);
                  setGuidanceMessage(buildRecommendationCopy({ categoryId, hasVerifiedExperience: false }));
                }}
              >
                <SelectTrigger><SelectValue placeholder="업종을 고르면 문구를 채워드립니다" /></SelectTrigger>
                <SelectContent>
                  {HELP_CONNECTION_CATEGORIES.map((category) => (
                    <SelectItem key={category.id} value={category.id}>{category.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => setGuidanceMessage(helpConnectionAdminManualReplyCopy.needsMoreInfo)}
              >
                추가 정보 요청 문구
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => setGuidanceMessage(helpConnectionAdminManualReplyCopy.guidanceSent)}
              >
                기본 답변 문구
              </Button>
            </div>

            <div className="space-y-1">
              <Label htmlFor="guidance-message">고객에게 보낼 메시지</Label>
              <Textarea
                id="guidance-message"
                rows={5}
                value={guidanceMessage}
                onChange={(e) => setGuidanceMessage(e.target.value)}
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={closeGuidanceDialog}>
              <XCircle className="mr-1 h-4 w-4" />취소
            </Button>
            <Button
              type="button"
              disabled={!guidanceMessage.trim() || patch.isPending}
              onClick={() =>
                guidanceTarget &&
                patch.mutate({ id: guidanceTarget.id, body: { guidanceMessage: guidanceMessage.trim() } })
              }
            >
              <MessageSquareReply className="mr-1 h-4 w-4" />답변 보내기
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
